import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientAllergies,
  qDeletedPatientAllergies,
  qAllergyReviewsByExtraction,
  softDeleteVoiceRecordingStmt,
  markConversationSummaryAppendedStmt,
  assembleTranscript,
  parseStringArrayColumn,
  classifyExtractedAllergy,
  dedupeAllergyMentions,
  normalizeName,
  unresolvedAllergyCount,
  extractionActionLabel,
  isExtractionActionDisabled,
  extractionDisabledReason,
  compactExtractionError,
  shouldAutoExtract,
  conversationSegmentStats,
  formatConversationDateTime,
  type PatientEncounterRow,
  type VisitVoiceRecordingRow,
  type VisitClinicalExtractionRow,
  type PatientAllergyRow,
  type VisitExtractionAllergyReviewRow,
} from '@helios/data-access';
import { applyExtractionSymptoms, applyExtractionDiagnoses } from './applyExtractionResults';
import { AudioRecordingPlayer } from './AudioRecordingPlayer';
import { TranscriptDrawer } from './TranscriptDrawer';
import {
  ExtractedAllergyReviewModal,
  type AllergyReviewItem,
  type AllergyReviewContext,
} from './ExtractedAllergyReviewModal';
import { NavIcon } from '../../../shell/shared';
import { useClinicalExtraction } from '../../../hooks/useClinicalExtraction';

/* ------------------------------------------------------------------ */
/*  ConversationCard (PRD.274)                                        */
/*                                                                    */
/*  One collapsible clinical conversation: its own recordings,        */
/*  transcript, auto-extraction (own hook + guard), summary,          */
/*  transcript drawer, and allergy review — fully scoped to this      */
/*  conversation. Never includes another conversation's recordings.   */
/* ------------------------------------------------------------------ */

const SECTION_KEY = 'chief_complaint';
const EXTRACTION_ENABLED = Boolean(import.meta.env.VITE_CLINICAL_EXTRACT_URL);

interface Props {
  encounter: PatientEncounterRow;
  conversationId: string;
  conversationStartedAt: string | null;
  /** When this conversation's first summary was appended to Chief Complaint. */
  summaryAppendedAt: string | null;
  /** This conversation's recordings, chronological (oldest first). */
  recordings: VisitVoiceRecordingRow[];
  /** This conversation's latest extraction row (or null). */
  extraction: VisitClinicalExtractionRow | null;
  expanded: boolean;
  onToggle: () => void;
  online: boolean;
  /** True only for a conversation just recorded in this session — gates the
   *  automatic allergy-review popup so it never fires on plain page load. */
  justRecorded?: boolean;
  brandTokens: Record<string, string>;
  /** Append a dated summary snapshot to the editable Chief Complaint. */
  onAppendSummary: (dateTime: string, summary: string) => void;
  getLocalUrl?: (recordingId: string) => string | null | undefined;
  onRetryUpload?: (recordingId: string) => void;
  canRunExtraction: boolean;
  canReviewAllergies: boolean;
  canEditAllergy: boolean;
  canDeleteRecording: boolean;
}

export function ConversationCard({
  encounter,
  conversationId,
  conversationStartedAt,
  summaryAppendedAt,
  recordings,
  extraction,
  expanded,
  onToggle,
  online,
  justRecorded = false,
  brandTokens,
  onAppendSummary,
  getLocalUrl,
  onRetryUpload,
  canRunExtraction,
  canReviewAllergies,
  canEditAllergy,
  canDeleteRecording,
}: Props) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const visitId = encounter.visit_id;

  const { data: allergies = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qPatientAllergies(encounter.patient_id),
  );
  const { data: deletedAllergies = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qDeletedPatientAllergies(encounter.patient_id),
  );
  const { data: reviewRows = [] } = usePowerSyncQuery<VisitExtractionAllergyReviewRow>(
    qAllergyReviewsByExtraction(extraction?.id ?? ''),
  );

  const extract = useClinicalExtraction();

  const [allergyModalOpen, setAllergyModalOpen] = useState(false);
  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(null);

  const conversationDateTime = formatConversationDateTime(
    conversationStartedAt ?? recordings[0]?.recorded_at ?? null,
  );

  const transcript = useMemo(
    () =>
      assembleTranscript(
        recordings.map((r) => ({ recorded_at: r.recorded_at, transcript: r.transcript })),
      ),
    [recordings],
  );
  const hasTranscript = transcript.trim().length > 0;

  // Reflect the extraction hook's IMMEDIATE API result so the card shows the
  // outcome without waiting for the PowerSync row to sync back. Once the synced
  // `extraction` row arrives it is authoritative and takes precedence.
  const hookResult = extract.status === 'success' ? extract.lastResult : null;
  const syncedCompleted = extraction?.status === 'completed';
  const completed = syncedCompleted || !!hookResult;
  const summaryText =
    (syncedCompleted ? (extraction?.summary ?? null) : null) ?? hookResult?.summary ?? null;
  const allergyMentionsRaw = syncedCompleted
    ? parseStringArrayColumn(extraction?.allergies_json)
    : (hookResult?.allergies ?? []);

  const allergyItems = useMemo<AllergyReviewItem[]>(() => {
    if (!completed) return [];
    const mentions = dedupeAllergyMentions(allergyMentionsRaw);
    const activeRefs = allergies.map((a) => ({ id: a.id, substance: a.substance }));
    const removedRefs = deletedAllergies.map((a) => ({ id: a.id, substance: a.substance }));
    return mentions.map((raw) => {
      const c = classifyExtractedAllergy(raw, activeRefs, removedRefs);
      const norm = normalizeName(raw);
      const review = reviewRows.find((r) => r.normalized_value === norm);
      return {
        rawValue: raw,
        normalizedValue: norm,
        classification: c.classification,
        matchedAllergyId: c.matchedAllergyId,
        matchedSubstance: c.matchedSubstance,
        matchedRemoved: c.matchedRemoved,
        reviewId: review?.id,
        status: review?.status ?? undefined,
      };
    });
    // Note: `allergyMentionsRaw` is intentionally keyed by its joined string
    // (a fresh array each render); the eslint react-hooks plugin is not enabled
    // in this project, so no exhaustive-deps suppression is needed here.
  }, [completed, allergyMentionsRaw.join(' '), allergies, deletedAllergies, reviewRows]);
  const pendingReviewCount = useMemo(() => unresolvedAllergyCount(allergyItems), [allergyItems]);

  // Auto-open the allergy review modal once, immediately after a conversation the
  // clinician JUST recorded in this session extracts (hook success) and surfaces
  // unresolved allergy mentions — so they are prompted to review without hunting
  // for the button. Gated on `justRecorded` so it NEVER pops on plain page load,
  // even though cards may auto-extract un-extracted transcripts on mount. Fires
  // once; never re-opens after the clinician closes it.
  const autoOpenedAllergyRef = useRef(false);
  useEffect(() => {
    if (autoOpenedAllergyRef.current || !justRecorded) return;
    if (extract.status === 'success' && pendingReviewCount > 0) {
      autoOpenedAllergyRef.current = true;
      setAllergyModalOpen(true);
    }
  }, [justRecorded, extract.status, pendingReviewCount]);

  // Apply extracted symptoms + diagnoses into the existing synced tables
  // (mirrors analyse). Guarded per extraction id.
  const appliedRef = useRef<Set<string>>(new Set());
  const applyEchoes = useCallback(
    async (extractionId: string, symptoms: string[], diagnoses: string[]) => {
      if (!db || !extractionId || appliedRef.current.has(extractionId)) return;
      appliedRef.current.add(extractionId);
      const ctx = {
        orgId,
        clinicId: encounter.clinic_id,
        patientId: encounter.patient_id,
        encounterId: encounter.id,
        userId: userId || null,
        extractionId,
        provider: 'siksha',
      };
      await applyExtractionSymptoms(db, symptoms, ctx);
      await applyExtractionDiagnoses(db, diagnoses, ctx);
    },
    [db, orgId, userId, encounter.clinic_id, encounter.patient_id, encounter.id],
  );
  useEffect(() => {
    if (extraction && extraction.status === 'completed' && extraction.id) {
      void applyEchoes(
        extraction.id,
        parseStringArrayColumn(extraction.symptoms_json),
        parseStringArrayColumn(extraction.diagnoses_json),
      );
    }
  }, [extraction, applyEchoes]);

  const runExtraction = useCallback(() => {
    if (!hasTranscript) return;
    const latest = recordings[recordings.length - 1];
    void (async () => {
      const summary = await extract.extract({
        orgId,
        patientId: encounter.patient_id,
        visitId,
        conversationId,
        sectionKey: SECTION_KEY,
        recordingId: latest?.id,
        transcript,
      });
      if (summary) await applyEchoes(summary.extractionId, summary.symptoms, summary.diagnoses);
    })();
  }, [
    extract,
    hasTranscript,
    recordings,
    orgId,
    encounter.patient_id,
    visitId,
    conversationId,
    transcript,
    applyEchoes,
  ]);

  // Conversation-scoped automatic extraction (PRD.274). One call per this
  // conversation's transcript key; independent of other conversations.
  const autoAttemptedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const transcriptKey = transcript.trim();
    const lastCompletedKey =
      extraction && extraction.status === 'completed'
        ? (extraction.source_transcript ?? '').trim()
        : null;
    const go = shouldAutoExtract({
      enabled: EXTRACTION_ENABLED,
      canRun: canRunExtraction,
      offline: !online,
      running: extract.status === 'submitting',
      hasTranscript,
      transcriptKey,
      lastCompletedKey,
      lastAttemptedKey: autoAttemptedKeyRef.current,
    });
    if (go) {
      autoAttemptedKeyRef.current = transcriptKey;
      runExtraction();
    }
  }, [
    transcript,
    extraction,
    extract.status,
    online,
    canRunExtraction,
    hasTranscript,
    runExtraction,
  ]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      const stmt = softDeleteVoiceRecordingStmt({ id, now: new Date().toISOString() });
      await db.execute(stmt.sql, stmt.params);
      setSelectedRecordingId((cur) => (cur === id ? null : cur));
    },
    [db],
  );

  // PRD.274 final rule — append ONE dated summary snapshot to Chief Complaint on
  // this conversation's first successful extraction. Guarded durably by the
  // conversation's summary_appended_at (survives reload/rehydration) + a session
  // ref, so re-render/reconnect/refresh never re-appends. Legacy conversations
  // (historical, backfilled) never auto-append. Refresh never rewrites CC — an
  // explicit button below re-appends an updated snapshot.
  const isLegacy = conversationId.startsWith('legacy_cc_');
  const appendedRef = useRef(false);
  const completedSummary = completed ? (summaryText ?? '').trim() : '';
  useEffect(() => {
    if (isLegacy || !db || !completedSummary) return;
    if (summaryAppendedAt || appendedRef.current) return;
    appendedRef.current = true;
    onAppendSummary(conversationDateTime, completedSummary);
    const s = markConversationSummaryAppendedStmt({
      id: conversationId,
      now: new Date().toISOString(),
    });
    void db.execute(s.sql, s.params);
  }, [
    isLegacy,
    db,
    completedSummary,
    summaryAppendedAt,
    conversationId,
    conversationDateTime,
    onAppendSummary,
  ]);

  // Shared extraction UI state for this conversation. `completed`/`summaryText`
  // (above) already prefer the immediate hook result over the synced row.
  const offline = !online;
  const running = extract.status === 'submitting';
  const failed = extract.status === 'error' || extraction?.status === 'failed';
  const autoPending =
    EXTRACTION_ENABLED &&
    canRunExtraction &&
    hasTranscript &&
    !offline &&
    !running &&
    !failed &&
    !completed;
  const extractionUi = {
    hasTranscript,
    running,
    offline,
    completed,
    failed,
    canRun: canRunExtraction,
    autoPending,
  };
  const extractionLabel = extractionActionLabel(extractionUi);
  const extractionDisabled = isExtractionActionDisabled(extractionUi);
  const extractionReason = extractionDisabledReason(extractionUi);
  const canExtractAction = EXTRACTION_ENABLED && canRunExtraction;
  const cardExtractionError =
    extract.status === 'error'
      ? compactExtractionError(true)
      : extraction?.status === 'failed'
        ? 'Extraction failed.'
        : null;
  const extractionStatusText = running
    ? 'Extracting…'
    : failed
      ? 'Extraction failed'
      : completed
        ? `Completed${extraction?.completed_at ? ` ${formatConversationDateTime(extraction.completed_at)}` : ''}`
        : autoPending
          ? 'Waiting to extract'
          : hasTranscript
            ? 'Not yet extracted'
            : 'Waiting for transcript';

  const selectedRecording = recordings.find((r) => r.id === selectedRecordingId) ?? null;
  const stats = conversationSegmentStats(recordings);
  const headerStatus = completed
    ? 'Extraction completed'
    : running
      ? 'Extracting…'
      : failed
        ? 'Extraction failed'
        : autoPending
          ? 'Waiting to extract'
          : 'Not yet extracted';

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-surface)',
      }}
    >
      {/* Disclosure header */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`Conversation ${conversationDateTime}, ${expanded ? 'expanded' : 'collapsed'}`}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: 'var(--sp-2) var(--sp-3)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms',
          }}
        >
          <NavIcon name="chevron-right" />
        </span>
        {/* Single-row header: title + inline meta. No summary preview on collapse. */}
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--text)',
              whiteSpace: 'nowrap',
            }}
          >
            Conversation — {conversationDateTime}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-2xs, 10px)',
              color: 'var(--text-muted)',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {/* PRD.274 simplification: no segment count for one-recording
                conversations; legacy multi-recording still shows it. */}
            {stats.segmentCount > 1 ? `${stats.segmentCount} recordings · ` : ''}
            {formatDuration(stats.totalDurationMs / 1000)} · {headerStatus}
            {canReviewAllergies && pendingReviewCount > 0
              ? ` · ${pendingReviewCount} ${pendingReviewCount === 1 ? 'allergy' : 'allergies'} to review`
              : ''}
          </span>
        </span>
      </button>

      {expanded && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
            padding: '0 var(--sp-3) var(--sp-3)',
          }}
        >
          {/* Summary with date/time metadata (date/time is NOT inside the summary text) */}
          {extraction?.summary && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={sectionLabel}>SUMMARY · {conversationDateTime}</span>
              {/* Reserve at least two lines so short AI summaries don't look cramped. */}
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text)',
                  lineHeight: 1.5,
                  minHeight: 'calc(1.5em * 2)',
                }}
              >
                {extraction.summary}
              </span>
              {/* Explicit re-append after a refresh — never rewrites Chief Complaint automatically. */}
              {canRunExtraction && completedSummary && summaryAppendedAt && (
                <button
                  type="button"
                  onClick={() => onAppendSummary(conversationDateTime, completedSummary)}
                  style={{
                    alignSelf: 'flex-start',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-2xs, 10px)',
                    textDecoration: 'underline',
                  }}
                >
                  Append updated summary to Chief Complaint
                </button>
              )}
            </div>
          )}

          {recordings.map((rec) => (
            <AudioRecordingPlayer
              key={rec.id}
              recording={rec}
              localBlobUrl={getLocalUrl?.(rec.id) ?? null}
              canDelete={canDeleteRecording}
              onDelete={() => void handleDelete(rec.id)}
              onRetryUpload={() => onRetryUpload?.(rec.id)}
              transcriptAvailable={(rec.transcript ?? '').trim().length > 0}
              onViewTranscript={() => setSelectedRecordingId(rec.id)}
              extractionLabel={extractionLabel}
              extractionDisabled={extractionDisabled}
              extractionDisabledReason={extractionReason}
              extractionRunning={running}
              extractionError={cardExtractionError}
              canExtract={canExtractAction}
              onExtract={runExtraction}
              allergyReviewCount={pendingReviewCount}
              onReviewAllergies={() => setAllergyModalOpen(true)}
              canReviewAllergies={canReviewAllergies}
            />
          ))}
        </div>
      )}

      <TranscriptDrawer
        open={selectedRecording !== null}
        onOpenChange={(o) => {
          if (!o) setSelectedRecordingId(null);
        }}
        recording={selectedRecording}
        recordings={recordings}
        brandTokens={brandTokens}
        extractionLabel={extractionLabel}
        extractionDisabled={extractionDisabled}
        extractionRunning={running}
        extractionError={cardExtractionError}
        extractionStatusText={extractionStatusText}
        canExtract={canExtractAction}
        onExtract={runExtraction}
        allergyReviewCount={pendingReviewCount}
        canReviewAllergies={canReviewAllergies}
        onReviewAllergies={() => setAllergyModalOpen(true)}
      />

      {allergyModalOpen && (
        <ExtractedAllergyReviewModal
          open={allergyModalOpen}
          onClose={() => setAllergyModalOpen(false)}
          items={allergyItems}
          canEditAllergy={canEditAllergy}
          conversationLabel={conversationDateTime}
          context={
            {
              orgId,
              clinicId: encounter.clinic_id,
              patientId: encounter.patient_id,
              visitId,
              extractionId: extraction?.id ?? '',
              sourceRecordingId: extraction?.source_recording_id ?? null,
              userId: userId || null,
            } satisfies AllergyReviewContext
          }
        />
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const sectionLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-2xs, 10px)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
};
